import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import Modal from './ui/Modal';
import DayraLogo from './ui/DayraLogo';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const isCompany = user?.role === 'company';
  const ordersPath = isCompany ? '/company/orders' : '/customer/orders';
  const myListingsPath = '/company/listings';

  const initials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
    : '';

  // Keep the nav search box in sync with the active query when the user is on /search.
  useEffect(() => {
    if (location.pathname === '/search') {
      setSearchQuery(searchParams.get('q') || '');
    } else {
      setSearchQuery('');
    }
  }, [location.pathname, searchParams]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileSearchOpen) mobileSearchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
    setDropdownOpen(false);
    setShowLogoutModal(false);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMobileSearchOpen(false);
  };

  return (
    <nav className="sticky top-0 z-40 bg-white border-b border-sand-200">
      <div className="h-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-6">

        {/* Logo */}
        <Link to="/" onClick={() => setMenuOpen(false)} className="shrink-0" aria-label="Dayra home">
          <DayraLogo />
        </Link>

        {/* Search (centered) + actions — desktop */}
        {!loading && (
          <div className="hidden md:grid flex-1 grid-cols-[1fr_auto] items-center gap-6 min-w-0">

            <div className="flex justify-center">
              <form onSubmit={submitSearch} className="group w-full max-w-md relative">
                <Search
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400 group-focus-within:text-terra-400 pointer-events-none transition-colors duration-200"
                />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search deep cleans, move-out, offices…"
                  aria-label="Search cleaning services"
                  className="w-full pl-9 pr-3 py-2 rounded-full bg-sand-100 border border-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:bg-white focus:border-terra-400/40 focus:ring-2 focus:ring-terra-400/15 transition-all duration-200"
                />
              </form>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <Link to="/listings" className="text-sm text-ink-700 hover:text-terra-400 transition-colors duration-200 whitespace-nowrap">
                Browse all
              </Link>

              {user ? (
                <>
                  <span className="w-px h-5 bg-sand-200" />

                  {isCompany && (
                    <Link to="/listings/create">
                      <Button variant="secondary" size="sm">+ List Service</Button>
                    </Link>
                  )}

                  <NotificationBell />

                  {/* Avatar dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setDropdownOpen(o => !o)}
                      className="w-8 h-8 rounded-full bg-terra-400 text-white text-xs font-semibold flex items-center justify-center hover:opacity-90 transition-opacity duration-200"
                      aria-label="Account menu"
                    >
                      {initials}
                    </button>

                    {dropdownOpen && (
                      <div className="absolute right-0 mt-2 w-44 bg-white border border-sand-200 rounded-lg shadow-sm py-1 z-50">
                        <div className="px-3 py-2.5 border-b border-sand-100">
                          <p className="text-xs font-medium text-ink-700 truncate">{user.first_name} {user.last_name}</p>
                          <p className="text-xs text-ink-400 capitalize mt-0.5">{user.role}</p>
                        </div>
                        <Link
                          to={ordersPath}
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center px-3 py-2 text-sm text-ink-700 hover:bg-sand-100 transition-colors"
                        >
                          Orders
                        </Link>
                        {isCompany && (
                          <Link
                            to={myListingsPath}
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center px-3 py-2 text-sm text-ink-700 hover:bg-sand-100 transition-colors"
                          >
                            My listings
                          </Link>
                        )}
                        <Link
                          to="/my-profile"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center px-3 py-2 text-sm text-ink-700 hover:bg-sand-100 transition-colors"
                        >
                          Profile
                        </Link>
                        <div className="border-t border-sand-100 mt-1 pt-1">
                          <button
                            onClick={() => { setDropdownOpen(false); setShowLogoutModal(true); }}
                            className="w-full text-left px-3 py-2 text-sm text-uae-red hover:bg-sand-100 transition-colors"
                          >
                            Log out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" size="sm">Login</Button></Link>
                  <Link to="/signup"><Button variant="primary" size="sm">Sign up</Button></Link>
                </>
              )}
            </div>
          </div>
        )}

        {loading && <div className="hidden md:block w-8 h-8 rounded-full bg-sand-200 animate-pulse ml-auto" />}

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-1 ml-auto">
          <button
            className="w-9 h-9 flex items-center justify-center text-ink-500 hover:text-ink-900 transition-colors duration-200"
            onClick={() => { setMobileSearchOpen(o => !o); setMenuOpen(false); }}
            aria-label="Toggle search"
          >
            {mobileSearchOpen ? <X size={19} /> : <Search size={19} />}
          </button>
          <button
            className="w-9 h-9 flex flex-col items-center justify-center gap-1.5"
            onClick={() => { setMenuOpen(o => !o); setMobileSearchOpen(false); }}
            aria-label="Toggle menu"
          >
            <span className={`block w-5 h-0.5 bg-ink-700 transition-all duration-200 origin-center ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-5 h-0.5 bg-ink-700 transition-all duration-200 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-ink-700 transition-all duration-200 origin-center ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile search row */}
      {mobileSearchOpen && (
        <div className="md:hidden border-t border-sand-200 px-4 py-3 bg-white">
          <form onSubmit={submitSearch} className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
            <input
              ref={mobileSearchInputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search deep cleans, move-out, offices…"
              aria-label="Search cleaning services"
              className="w-full pl-9 pr-3 py-2.5 rounded-full bg-sand-100 border border-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:bg-white focus:border-terra-400/40 focus:ring-2 focus:ring-terra-400/15 transition-all duration-200"
            />
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-50 bg-white border-t border-sand-200 flex flex-col p-6 gap-1 overflow-y-auto md:hidden">
          {user && (
            <div className="flex items-center gap-3 pb-4 mb-2 border-b border-sand-200">
              <div className="w-9 h-9 rounded-full bg-terra-400 text-white text-sm font-semibold flex items-center justify-center shrink-0">
                {initials}
              </div>
              <div>
                <p className="text-sm font-medium text-ink-700">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-ink-400 capitalize mt-0.5">{user.role}</p>
              </div>
            </div>
          )}

          {!loading && <Link
            to="/listings"
            onClick={() => setMenuOpen(false)}
            className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors"
          >
            Browse all services
          </Link>}

          {!loading && user ? (
            <>
              <Link to="/notifications" onClick={() => setMenuOpen(false)} className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors">
                Notifications
              </Link>
              <Link to={ordersPath} onClick={() => setMenuOpen(false)} className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors">
                Orders
              </Link>
              {isCompany && (
                <>
                  <Link to={myListingsPath} onClick={() => setMenuOpen(false)} className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors">
                    My listings
                  </Link>
                  <Link to="/listings/create" onClick={() => setMenuOpen(false)} className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors">
                    + List Service
                  </Link>
                </>
              )}
              <Link to="/my-profile" onClick={() => setMenuOpen(false)} className="text-base text-ink-700 hover:text-terra-400 py-3 border-b border-sand-100 transition-colors">
                Profile
              </Link>
              <button
                onClick={() => { setMenuOpen(false); setShowLogoutModal(true); }}
                className="text-left text-base text-uae-red py-3 mt-2"
              >
                Log out
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-3 mt-4">
              <Link to="/login" onClick={() => setMenuOpen(false)}>
                <Button variant="secondary" className="w-full justify-center">Login</Button>
              </Link>
              <Link to="/signup" onClick={() => setMenuOpen(false)}>
                <Button variant="primary" className="w-full justify-center">Sign up</Button>
              </Link>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Log out?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowLogoutModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleLogout}>Log out</Button>
          </>
        }
      >
        <p className="text-sm text-ink-400 leading-relaxed">Are you sure you want to log out?</p>
      </Modal>
    </nav>
  );
}
